import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Trash2 } from 'lucide-react'
import { curationsService, validateTitle, validateDescription } from '../../lib/curations'
import { useNavigate } from 'react-router-dom'

interface NewCurationModalProps {
  isOpen: boolean
  onClose: () => void
  userId: string
  username: string
  onCurationCreated?: () => void
  curationToEdit?: {
    id: string
    title: string
    narrative?: string
    highlighted_image_url?: string
    layout_type: 'grid' | 'masonry'
    rounded_corners?: boolean
    status?: 'private' | 'public' | 'published'
  }
}

export function NewCurationModal({ isOpen, onClose, userId, username, onCurationCreated, curationToEdit }: NewCurationModalProps) {
  const navigate = useNavigate()
  const isEditMode = !!curationToEdit
  const [title, setTitle] = useState(curationToEdit?.title || '')
  const [narrative, setNarrative] = useState(curationToEdit?.narrative || '')
  // Default new curations to public; the toggle opts into private.
  const [isPrivate, setIsPrivate] = useState(curationToEdit?.status === 'private')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Update state when curationToEdit changes
  useEffect(() => {
    if (curationToEdit) {
      setTitle(curationToEdit.title)
      setNarrative(curationToEdit.narrative || '')
      setIsPrivate(curationToEdit.status === 'private')
    } else {
      setTitle('')
      setNarrative('')
      setIsPrivate(false)
    }
    setError(null)
  }, [curationToEdit, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate title
    const titleValidation = validateTitle(title)
    if (!titleValidation.valid) {
      setError(titleValidation.error || 'Invalid title')
      return
    }

    // Validate narrative
    const narrativeValidation = validateDescription(narrative)
    if (!narrativeValidation.valid) {
      setError(narrativeValidation.error || 'Invalid description')
      return
    }

    setIsSubmitting(true)

    try {
      if (isEditMode && curationToEdit) {
        // Update existing curation
        await curationsService.updateCuration(curationToEdit.id, {
          title,
          narrative: narrative || undefined,
        })

        // Apply visibility. Private on → private; off → public, unless it's
        // already published (a metadata edit shouldn't drop it from the feed).
        const current = curationToEdit.status
        const target = isPrivate ? 'private' : (current === 'published' ? current : 'public')
        if (target !== current && (target === 'private' || target === 'public')) {
          await curationsService.setCurationStatus(curationToEdit.id, target)
        }

        // Call the callback if provided
        if (onCurationCreated) {
          onCurationCreated()
        }

        // Close modal
        onClose()
      } else {
        // Create new curation
        const curation = await curationsService.createCuration(userId, {
          title,
          narrative: narrative || undefined,
          status: isPrivate ? 'private' : 'public',
        })

        // Navigate to the curation detail page
        navigate(`/profile/${username}/issues/${curation.slug}`)

        // Call the callback if provided
        if (onCurationCreated) {
          onCurationCreated()
        }

        // Close modal
        onClose()
      }
    } catch (err: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} curation:`, err)
      setError(err.message || `Failed to ${isEditMode ? 'update' : 'create'} curation`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!curationToEdit || !isEditMode) return

    const confirmed = window.confirm('Are you sure you want to delete this curation? This action cannot be undone.')
    if (!confirmed) return

    setIsDeleting(true)
    try {
      await curationsService.deleteCuration(curationToEdit.id)

      // Navigate back to profile curated tab
      navigate(`/profile/${username}?tab=issues`)

      // Call the callback if provided
      if (onCurationCreated) {
        onCurationCreated()
      }

      // Close modal
      onClose()
    } catch (err: any) {
      console.error('Error deleting curation:', err)
      setError(err.message || 'Failed to delete curation')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleClose = () => {
    if (isSubmitting || isDeleting) return
    setTitle('')
    setNarrative('')
    setIsPrivate(false)
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="absolute inset-0 bg-black/50"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b">
            <h2
              className="text-2xl font-light"
              style={{ fontFamily: "Canela, Georgia, serif" }}
            >
              {isEditMode ? 'Edit Curation' : 'Create Curation'}
            </h2>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Title */}
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}
                >
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Give your curation a name"
                  required
                  minLength={3}
                  maxLength={100}
                  disabled={isSubmitting}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent disabled:opacity-50 disabled:bg-gray-50"
                  style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {title.length}/100 characters
                </p>
              </div>

              {/* Description */}
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}
                >
                  Description (optional)
                </label>
                <textarea
                  value={narrative}
                  onChange={(e) => setNarrative(e.target.value)}
                  placeholder="What's this curation about?"
                  rows={4}
                  maxLength={1500}
                  disabled={isSubmitting}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent disabled:opacity-50 disabled:bg-gray-50 resize-none"
                  style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {narrative.length}/1,500 characters
                </p>
              </div>

              {/* Visibility — new curations are public by default; opt into private */}
              <div className="flex items-center justify-between gap-4 p-3 border border-gray-200 rounded-lg">
                <div>
                  <p
                    className="text-sm font-medium text-gray-800"
                    style={{ fontFamily: "'GT America Trial Regular', -apple-system, sans-serif" }}
                  >
                    Make this curation private
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {isPrivate
                      ? 'Only you can see it — hidden from your profile for everyone else.'
                      : 'Anyone can view it on your profile or with the link.'}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isPrivate}
                  aria-label="Make this curation private"
                  onClick={() => setIsPrivate(v => !v)}
                  disabled={isSubmitting}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                    isPrivate ? 'bg-[#57241F]' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      isPrivate ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t bg-white px-6 py-4 flex justify-between items-center">
              {/* Left side - Delete button (only in edit mode) */}
              <div>
                {isEditMode && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isSubmitting || isDeleting}
                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                    style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                  >
                    {isDeleting ? (
                      <>
                        <div className="h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                        <span>Deleting...</span>
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4" />
                        <span>Delete</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Right side - Cancel and Save/Create buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting || isDeleting}
                  className="hidden sm:block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || isDeleting || !title.trim()}
                  className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  style={{ fontFamily: "'GT America Mono', 'Courier New', monospace" }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : isEditMode ? (
                    <>
                      <span className="hidden sm:inline">Save Changes</span>
                      <span className="sm:hidden">Save</span>
                    </>
                  ) : (
                    'Create'
                  )}
                </button>
              </div>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}
